import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiSearch } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { DashboardContent } from '@/layouts/dashboard';
import { Loader } from '@/components/custom/loader';
import { EmptyState } from '@/components/custom/empty-state';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { liveChallengesApi } from '@/services/liveChallenges';

const STATUS_CONFIG = {
  draft: { label: 'Rascunho', variant: 'neutral-lightest' },
  lobby: { label: 'Lobby', variant: 'warning' },
  in_progress: { label: 'Em Andamento', variant: 'primary-light' },
  finished: { label: 'Finalizado', variant: 'success-light' }
};

const STATUS_FILTERS = [
  { value: 'all', label: 'Todos' },
  { value: 'draft', label: 'Rascunho' },
  { value: 'lobby', label: 'Lobby' },
  { value: 'in_progress', label: 'Em Andamento' },
  { value: 'finished', label: 'Finalizado' }
];

function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  return (
    <Badge
      variant={config.variant}
      size="xs"
    >
      {config.label}
    </Badge>
  );
}

function ChallengeRow({ challenge, onRefresh }) {
  const navigate = useNavigate();
  const { id, title, taxonomy, status, created_at, total_questions } = challenge;

  const formattedDate = created_at ? new Date(created_at).toLocaleDateString('pt-BR') : '—';

  const handleLaunch = async () => {
    try {
      await liveChallengesApi.openLobby(id);
      toast.success('Lobby aberto com sucesso!');
      onRefresh();
    } catch {
      toast.error('Erro ao abrir lobby.');
    }
  };

  return (
    <li className="hover:bg-primary-lightest/30 flex flex-col gap-3 border-b border-neutral-lightest bg-white px-5 py-3 transition-colors md:flex-row md:items-center md:gap-6">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-3">
          <h3 className="truncate text-sm font-semibold text-neutral-dark">{title}</h3>
          <StatusBadge status={status} />
        </div>
        <p className="mt-0.5 text-xs text-neutral">
          {taxonomy?.name || '—'} &middot; {total_questions ?? 0} questões
        </p>
      </div>

      <div className="hidden w-32 shrink-0 text-xs text-neutral-light md:block">{formattedDate}</div>

      <div className="flex flex-wrap justify-end gap-2 md:w-56">
        {status === 'draft' && (
          <>
            <Button
              type="button"
              variant="outline"
              size="xs"
              onClick={() => navigate(`/admin/desafio-ao-vivo/editar/nome/${id}`)}
            >
              Editar
            </Button>
            <Button
              type="button"
              variant="default"
              size="xs"
              onClick={handleLaunch}
            >
              Lançar
            </Button>
          </>
        )}

        {(status === 'lobby' || status === 'in_progress') && (
          <Button
            type="button"
            variant="default"
            size="xs"
            onClick={() => navigate(`/admin/desafio-ao-vivo/painel/${id}`)}
          >
            Painel
          </Button>
        )}

        {status === 'finished' && (
          <Button
            type="button"
            variant="success"
            size="xs"
            onClick={() => navigate(`/admin/desafio-ao-vivo/painel/${id}`)}
          >
            Resultados
          </Button>
        )}
      </div>
    </li>
  );
}

export default function LiveChallengeList() {
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const breadcrumbs = {
    title: 'Aprendizado Dinâmico',
    nav: [
      {
        route: '/admin/desafio-ao-vivo',
        name: 'Desafio ao Vivo',
        isActive: true
      }
    ]
  };

  const normalizeJsonApi = items =>
    items.map(item => ({
      id: item.id,
      ...Object.fromEntries(Object.entries(item.attributes).map(([k, v]) => [k.replace(/-/g, '_'), v]))
    }));

  const fetchChallenges = async () => {
    setLoading(true);
    try {
      const response = await liveChallengesApi.index();
      const items = response?.data ?? [];
      setChallenges(normalizeJsonApi(items));
    } catch {
      toast.error('Erro ao carregar desafios ao vivo.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChallenges();
  }, []);

  const filteredChallenges = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return challenges.filter(c => {
      if (statusFilter !== 'all' && c.status !== statusFilter) return false;
      if (normalizedSearch && !c.title?.toLowerCase().includes(normalizedSearch)) return false;
      return true;
    });
  }, [challenges, statusFilter, searchTerm]);

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-bold text-neutral-dark">Desafios ao Vivo</h2>
        <Button
          asChild
          variant="default"
          size="sm"
        >
          <Link to="/admin/desafio-ao-vivo/criar/nome">Criar Desafio</Link>
        </Button>
      </div>

      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <Tabs
          value={statusFilter}
          onValueChange={setStatusFilter}
        >
          <TabsList>
            {STATUS_FILTERS.map(f => (
              <TabsTrigger
                key={f.value}
                value={f.value}
              >
                {f.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="relative md:w-72">
          <FiSearch
            aria-hidden
            className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-light"
          />
          <Input
            startIcon
            type="search"
            placeholder="Buscar por título..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <Loader />
      ) : filteredChallenges.length > 0 ? (
        <ul className="overflow-hidden rounded-xl bg-white shadow-dropshadow-sm [&>li:last-child]:border-b-0">
          {filteredChallenges.map(challenge => (
            <ChallengeRow
              key={challenge.id}
              challenge={challenge}
              onRefresh={fetchChallenges}
            />
          ))}
        </ul>
      ) : challenges.length === 0 ? (
        <EmptyState type="data" />
      ) : (
        // EmptyState não tem o type 'search' — caía no default 'Insira um texto.'
        // sem imagem. Reaproveitamos a ilustração de 'data' com texto explícito.
        <EmptyState
          type="data"
          text="Nenhum desafio encontrado para este filtro."
        />
      )}
    </DashboardContent>
  );
}
